import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationService } from '../../Layout/navigation.service';
import { ContactRoleService } from '../../Role/contact.role.service';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';
import { PanelChevronComponent } from '../../UI-Lib/Weissman-Panel/panel.chevron.component';
import { ContactsUpgradeService } from '../contacts.service.upgrade';
import { RestrictData, RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { UserInstanceService } from '../../User/userInstance.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ContactPanelBodyActionsComponent } from './Body-Actions/panelBodyActions.component';
import { ContactListComponent } from './List/contactList.component';

@Component({
    selector: 'contacts-panel',
    templateUrl: './contactPanel.component.html',
    styleUrls: ['./contactPanel.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        PanelChevronComponent,
        AceLoaderComponent,
        ContactPanelBodyActionsComponent,
        ContactListComponent,
    ],
})
export class ContactPanelComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _roleService: ContactRoleService,
        private readonly _contactsService: ContactsUpgradeService,
        private readonly _restrictService: RestrictService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _navigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _navigationService: NavigationService,
    ) {}

    @Input() entity;

    isEntityCompanyBased: boolean = false;

    contacts = [];
    roles = [];
    isLoading: boolean = false;
    showBody: boolean = false;
    isEdit: boolean = false;
    editLock: boolean = false;
    hasEditPermission: boolean = false;
    hasViewOrEditPermission: boolean = false;
    isConsultantUser: boolean = false;
    isCompany: boolean = false;
    showAll: boolean = false;

    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this._contactsService.contactCache$.pipe(takeUntil(this._destroy$)).subscribe((contacts) => {
            this.contacts = contacts;
        });

        this.isEntityCompanyBased =
            this.entity &&
            (this.entity.type === 'site' || this.entity.type === 'company' || this.entity.type === 'parcel');
        this.showAll = false;
        this.getViewPermission();
        this.getEditPermissionForContactAndEntity();

        this._navigationService.globalEditMode$.pipe(takeUntil(this._destroy$)).subscribe((editMode) => {
            this.editLock = editMode;
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    expandOrCollapse(): void {
        if (this.isEdit) {
            return;
        }

        this.showBody = !this.showBody;

        if (this.showBody) {
            this.load();
        }
    }

    async load(): Promise<boolean> {
        this.isLoading = true;

        if (!this.entity) {
            this.showBody = false;
            this.isLoading = false;
            return false;
        } else {
            this.isCompany = this.entity.type === 'company';
        }

        try {
            if (this.isCompany) {
                this.isConsultantUser = await this._restrictService.isConsultantUser(this.entity.id);
            }

            const contacts = await this._contactsService.getByEntity(this.entity.id, this.entity.type, this.showAll);

            const roles = await this._roleService.get();
            this.roles = roles.filter((x) => !!x[this.entity.type]);

            contacts.forEach((x) => {
                x.contactRoleAssociations = x.contactRoleAssociations || [];
            });

            this._contactsService.contacts = contacts;
        } catch (err) {
            this.showBody = false;
        } finally {
            this.isLoading = false;
        }
    }

    edit(): void {
        this.isEdit = true;
        this._navigationService.enableNavWarning('You have unsaved changes. Are you sure you want to leave this page?');
    }

    cancel(): void {
        this.isEdit = false;
        this._navigationService.disableNavWarning();
    }

    showAllContacts(): void {
        this.showAll = !this.showAll;
        this.load();
    }

    async getEditPermissionForContactAndEntity(): Promise<void> {
        //contacts for company, site and parcel
        if (this.isEntityCompanyBased) {
            const restrictionData = {
                isCompany: this.entity.type.toLowerCase() === 'company',
                entityId: this.entity.id,
                flag: Core.AccessRightsEnum.Write,
            } as RestrictData;

            if (this.entity.type === 'parcel') {
                restrictionData.entityId = +this._navigationServiceHandler.getQuerystringParam('siteId');
            }

            try {
                this.hasEditPermission = await this._restrictService.hasPermission(restrictionData, true);
            } catch (err) {
                console.warn('checkEntity - error', err);
            }
        } else {
            this.hasEditPermission =
                this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT) &&
                this._restrictService.isInRole(Roles.JURISDICTIONSETUPS);
        }
    }

    async getViewPermission(): Promise<void> {
        const restrictionData = this._restrictService.getRestrictionDataForEntity(
            this.entity,
            Core.AccessRightsEnum.Read,
        );
        try {
            const result = await this._restrictService.hasPermission(restrictionData);
            this.hasViewOrEditPermission =
                result && (this.isEntityCompanyBased || this._userInstanceService.isCurrentInstanceRyan());
        } catch (err) {
            console.warn('checkEntity - error', err);
        }
    }
}
